import { isEmptyObject } from 'es-toolkit';
import {
  type BigDecimalRangeDto,
  isRecord,
  type ListingSortOption,
  mergeState,
  PropertyMarketingType,
  type SearchListingsFilters,
  type SearchListingsOptions,
} from '@ev-tech/search-modules-api';

import { getSortValues } from './stringUtils';
import { parseSearchParams } from './searchParams';
import { SearchMode } from '../hooks/stores/search/slices/searchModeSlice';
import { type MapMode } from '../hooks/stores/search/slices/mapInteractionSlice';
import { SortingOptionId } from '../enums/enums';
import { DEFAULT_FILTERS, DEFAULT_OPTIONS } from '../consts/searchDefaults';
import { SORT_BY_OPTIONS } from '../consts/consts';

export { DEFAULT_FILTERS, DEFAULT_OPTIONS } from '../consts/searchDefaults';

export const isNonEmptyObject = (value: unknown): value is Record<string, unknown> => {
  return isRecord(value) && !isEmptyObject(value);
};

export function containsTruthyRangeValue(
  range: BigDecimalRangeDto | undefined,
  verifyMinOnly: true,
): range is BigDecimalRangeDto & { min: number };
export function containsTruthyRangeValue(range?: BigDecimalRangeDto, verifyMinOnly?: false): boolean;
export function containsTruthyRangeValue(range?: BigDecimalRangeDto, verifyMinOnly = false): boolean {
  if (verifyMinOnly) {
    return (range?.min ?? 0) > 0;
  }

  return (range?.min ?? 0) > 0 || (range?.max ?? 0) > 0;
}

export const getNewSortingOptions = (
  propertyMarketingType: PropertyMarketingType,
  initialSortingOptionId: SortingOptionId,
): SortingOptionId => {
  const initialSortingOption = getListingSortOptionById(initialSortingOptionId);
  const { sortOrder, sortField } = getSortValues({ sortingOption: initialSortingOption });

  if (sortField && ['SALES_PRICE', 'RENT'].includes(sortField)) {
    if (propertyMarketingType === PropertyMarketingType.sale) {
      return sortOrder === 'ASC' ? SortingOptionId.PRICE_ASC : SortingOptionId.PRICE_DESC;
    } else {
      return sortOrder === 'ASC' ? SortingOptionId.RENT_ASC : SortingOptionId.RENT_DESC;
    }
  }

  return initialSortingOptionId;
};

export const getDefaultSortingOptionId = (propertyMarketingType: PropertyMarketingType = PropertyMarketingType.sale): SortingOptionId => {
  return propertyMarketingType === PropertyMarketingType.rent ? SortingOptionId.RENT_DESC : SortingOptionId.PRICE_DESC;
};

export const getListingSortOptionById = (sortingOptionId?: SortingOptionId): ListingSortOption | undefined => {
  return SORT_BY_OPTIONS.find((option) => option.id === sortingOptionId)?.sortingOption;
};

export const getSortingOptionIdFromFilters = ({
  sortingOptions,
  searchMode,
}: Pick<SearchListingsFilters, 'sortingOptions'> & { searchMode?: SearchMode }): SortingOptionId | undefined => {
  // SortingOptionId.PREFERENCES is used for smart_sort and ai_sort
  // If the search mode is ai, we want to use SortingOptionId.PREFERENCES
  if (sortingOptions?.[0] === SortingOptionId.PREFERENCES) {
    return searchMode === SearchMode.AI ? SortingOptionId.PREFERENCES : SortingOptionId.SMART_SORT;
  }
  return SORT_BY_OPTIONS.find((option) => option.sortingOption === sortingOptions?.[0])?.id;
};

export const resolveInitialSortingOptionId = ({
  sortingOptionId,
  propertyMarketingType,
  searchMode,
  isSmartSortEnabled,
  hasSmartSortPreferences,
  hasAiSearchPreferences,
}: {
  sortingOptionId?: SortingOptionId;
  propertyMarketingType?: PropertyMarketingType;
  searchMode?: SearchMode;
  isSmartSortEnabled: boolean;
  hasSmartSortPreferences: boolean;
  hasAiSearchPreferences: boolean;
}): SortingOptionId => {
  const canUseSmartSort = isSmartSortEnabled && hasSmartSortPreferences;
  const canUseAiPreferenceSorting = searchMode === SearchMode.AI && hasAiSearchPreferences;

  if (sortingOptionId) {
    if (sortingOptionId === SortingOptionId.SMART_SORT && !canUseSmartSort) {
      return getDefaultSortingOptionId(propertyMarketingType);
    }

    if (sortingOptionId === SortingOptionId.PREFERENCES && !canUseAiPreferenceSorting) {
      return getDefaultSortingOptionId(propertyMarketingType);
    }

    return sortingOptionId;
  }

  if (canUseSmartSort && searchMode !== SearchMode.AI) {
    return SortingOptionId.SMART_SORT;
  }

  return getDefaultSortingOptionId(propertyMarketingType);
};

export const createSearchStateFromUrl = (
  url: string,
  initialFilters?: SearchListingsFilters,
  initialOptions?: SearchListingsOptions,
): {
  filters: SearchListingsFilters;
  options: Required<SearchListingsOptions>;
  placeName?: string;
  searchMode?: SearchMode;
  mapMode?: MapMode;
  sortingOptionId?: SortingOptionId;
} => {
  const {
    filters: parsedFilters,
    options: parsedOptions,
    additionalParams: { placeName, searchMode, mapMode, sortingOptionId },
  } = parseSearchParams(url);

  const newFilters = initialFilters ? mergeState(initialFilters, parsedFilters) : parsedFilters;
  const filters = { ...DEFAULT_FILTERS, ...newFilters };
  const options = {
    ...DEFAULT_OPTIONS,
    ...initialOptions,
    ...parsedOptions,
  };

  return {
    filters,
    options,
    placeName,
    searchMode,
    mapMode,
    sortingOptionId: sortingOptionId ?? getSortingOptionIdFromFilters({ sortingOptions: filters.sortingOptions, searchMode }),
  };
};
